// CourierShell.tsx - Marco de la app móvil del courier (diseño ITOBOX Platform 2.0):
// encabezado de color de la empresa, contenido y barra inferior con el botón de
// escanear al centro. En computadora se muestra como una columna centrada.
import React, { ReactNode } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { Home, Truck, ScanLine, DollarSign, Gauge, ChevronLeft, LogOut } from 'lucide-react';
import { useTenant } from '../../contexts/TenantContext';
import { useAuth } from '../../shared/hooks/useAuth';
import '../platformTheme.css';
import AccountStatusNotice from '../AccountStatusNotice';
import InstallAppBanner from '../InstallAppBanner';

export const BRAND_FALLBACK = '#E50914';

/** Color principal de la empresa (rojo ITOBOX si no tiene) */
export function useBrandColor(): string {
    const { tenant } = useTenant();
    const c = tenant?.branding?.primary_color;
    return c && /^#[0-9a-f]{6}$/i.test(c) ? c : BRAND_FALLBACK;
}

const TABS = [
    { to: '/courier/dashboard', label: 'Inicio', icon: Home },
    { to: '/courier/deliveries', label: 'Entregas', icon: Truck },
    { to: '/courier/scanner', label: 'Escanear', icon: ScanLine, center: true },
    { to: '/courier/earnings', label: 'Ganancias', icon: DollarSign },
    { to: '/courier/performance', label: 'Desempeño', icon: Gauge },
];

interface ShellProps {
    /** Contenido del encabezado de color (título, saludo, tarjetas de resumen) */
    header: ReactNode;
    children: ReactNode;
    /** Muestra "←" para volver en lugar de la barra inferior activa */
    back?: string;
    /** Mostrar el botón de cerrar sesión (solo en Inicio) */
    showLogout?: boolean;
}

const CourierShell: React.FC<ShellProps> = ({ header, children, back, showLogout }) => {
    const brand = useBrandColor();
    const navigate = useNavigate();
    const { logout } = useAuth();

    const handleLogout = async () => {
        try { await logout(); } finally { navigate('/login'); }
    };

    return (
        <div className="min-h-screen bg-[#E9EAEE] flex justify-center" style={{ fontFamily: 'Poppins, system-ui, sans-serif' }}>
            <div className="relative w-full max-w-[480px] min-h-screen bg-[#F5F6F8] flex flex-col shadow-sm">
                <header
                    className="text-white px-5 pt-6 pb-5 rounded-b-3xl"
                    style={{ background: brand, paddingTop: 'max(1.5rem, env(safe-area-inset-top))' }}
                >
                    <div className="flex items-center justify-between mb-1 min-h-[20px]">
                        {back ? (
                            <button onClick={() => navigate(back)} className="flex items-center gap-1 text-sm text-white/80">
                                <ChevronLeft size={18} /> Volver
                            </button>
                        ) : <span />}
                        {showLogout && (
                            <button onClick={handleLogout} aria-label="Cerrar sesión" className="p-1.5 rounded-lg bg-white/15 text-white">
                                <LogOut size={16} />
                            </button>
                        )}
                    </div>
                    {header}
                </header>

                <main style={{ ['--p2-brand' as any]: brand }} className="p2 flex-1 px-4 pt-4 pb-28 flex flex-col gap-3">
                    <AccountStatusNotice />
                    <InstallAppBanner />
                    {children}
                </main>

                <nav
                    className="fixed bottom-0 w-full max-w-[480px] bg-white border-t border-[#EEF0F3] flex justify-around items-start pt-2 z-30"
                    style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
                >
                    {TABS.map(({ to, label, icon: Icon, center }) => (
                        <NavLink key={to} to={to} className="flex flex-col items-center gap-1 w-[68px] text-[10px] font-medium no-underline hover:no-underline" style={{ textDecoration: 'none' }}>
                            {({ isActive }) => center ? (
                                <>
                                    <span
                                        className="w-[54px] h-[54px] -mt-7 rounded-full flex items-center justify-center border-4 border-white"
                                        style={{ background: brand, boxShadow: `0 8px 20px ${brand}59` }}
                                    >
                                        <Icon size={24} color="#FFFFFF" />
                                    </span>
                                    <span style={{ color: isActive ? brand : '#6B7280' }}>{label}</span>
                                </>
                            ) : (
                                <>
                                    <Icon size={22} color={isActive ? brand : '#6B7280'} />
                                    <span style={{ color: isActive ? brand : '#6B7280', fontWeight: isActive ? 600 : 500 }}>{label}</span>
                                </>
                            )}
                        </NavLink>
                    ))}
                </nav>
            </div>
        </div>
    );
};

export default CourierShell;

// ── Piezas comunes ──────────────────────────────────────────────────────────

export const Card: React.FC<{ children: ReactNode; className?: string; style?: React.CSSProperties }> = ({ children, className = '', style }) => (
    <div className={`bg-white border border-[#EEF0F3] rounded-[18px] p-4 shadow-[0_4px_16px_rgba(0,0,0,0.06)] ${className}`} style={style}>{children}</div>
);

/** Recuadro translúcido para cifras dentro del encabezado de color */
export const HeaderStat: React.FC<{ label: string; value: ReactNode }> = ({ label, value }) => (
    <div className="flex-1 bg-white/15 rounded-2xl p-3 min-w-0">
        <div className="text-[12px] text-white/80 truncate">{label}</div>
        <div className="text-[22px] font-extrabold leading-tight truncate">{value}</div>
    </div>
);

const PILL: Record<string, string> = {
    gray: 'bg-[#F3F4F6] text-[#374151]', blue: 'bg-[#E0EDFF] text-[#1D4ED8]', amber: 'bg-[#FDF1DD] text-[#9A5B0B]',
    orange: 'bg-[#FFEDD5] text-[#9A3412]', green: 'bg-[#E5F6EA] text-[#15803D]', red: 'bg-[#FDE8E9] text-[#B80710]',
    indigo: 'bg-[#ECE9FE] text-[#4C1D95]',
};
export const Pill: React.FC<{ tone?: keyof typeof PILL; children: ReactNode }> = ({ tone = 'gray', children }) => (
    <span className={`inline-flex items-center h-6 px-2.5 rounded-full text-[11px] font-semibold whitespace-nowrap ${PILL[tone]}`}>{children}</span>
);

export const DELIVERY_STATUS: Record<string, { label: string; tone: keyof typeof PILL }> = {
    assigned: { label: 'Asignado', tone: 'indigo' },
    pending: { label: 'Asignado', tone: 'indigo' },
    in_route: { label: 'En ruta', tone: 'orange' },
    delivered: { label: 'Entregado', tone: 'green' },
    failed: { label: 'Fallido', tone: 'red' },
    returned: { label: 'Devuelto', tone: 'gray' },
};

export const Loading: React.FC<{ text?: string }> = ({ text = 'Cargando...' }) => {
    const brand = useBrandColor();
    return (
        <div className="flex flex-col items-center justify-center py-16 gap-3 text-[#6B7280] text-sm">
            <div className="w-10 h-10 rounded-full border-4 border-[#E5E7EB] animate-spin" style={{ borderTopColor: brand }} />
            {text}
        </div>
    );
};

export const ErrorBox: React.FC<{ message: string; onRetry: () => void }> = ({ message, onRetry }) => (
    <Card className="text-center py-8">
        <p className="font-semibold text-[#111111] mb-1">No se pudo cargar</p>
        <p className="text-sm text-[#6B7280] mb-4">{message}</p>
        <button onClick={onRetry} className="h-11 px-6 rounded-xl bg-[#111111] text-white text-sm font-semibold">Reintentar</button>
    </Card>
);

export const Empty: React.FC<{ icon: ReactNode; text: string }> = ({ icon, text }) => (
    <Card className="text-center py-10 text-[#9CA3AF]">
        <div className="flex justify-center mb-3">{icon}</div>
        <p className="text-sm">{text}</p>
    </Card>
);

/** Colones: ₡9.500 */
export const crc = (n: number) => `₡${Math.round(n || 0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`;

/** "viernes, 9 de octubre" → "Viernes, 9 de octubre" */
export const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** Enlace de Google Maps / Waze para una dirección */
export const mapsUrl = (address: string) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
